/**
 * The composer's height (spec §3): the question field starts at 40 px and
 * grows with its text up to max(120 px, 36 % of the visible viewport). In a
 * constrained layout (a phone in landscape with the keyboard open) the
 * height actually available wins, so the controls stay usable; beyond the
 * limit the field scrolls inside itself.
 */

export const FIELD_MIN_PX = 40;
const NOMINAL_FLOOR_PX = 120;
const NOMINAL_SHARE = 0.36;
/** The conversation keeps at least this much height while the field grows. */
export const TRANSCRIPT_RESERVE_PX = 48;

/** max(120 px, 36 % of the visible viewport height). */
export const nominalMaxHeight = (visibleHeight: number) =>
  Math.max(NOMINAL_FLOOR_PX, Math.round(visibleHeight * NOMINAL_SHARE));

/**
 * The height the field may take without pushing the conversation below its
 * reserve: the conversation's height plus the field's own (their sum does
 * not change while the field grows), minus the reserve.
 */
export const availableHeight = ({
  transcriptHeight,
  fieldHeight,
  reserve = TRANSCRIPT_RESERVE_PX,
}: {
  transcriptHeight: number;
  fieldHeight: number;
  reserve?: number;
}) => Math.max(0, Math.round(transcriptHeight + fieldHeight - reserve));

/** The field's limit: the nominal maximum, or less when less is available. */
export const fieldMaxHeight = ({
  visibleHeight,
  available,
}: {
  visibleHeight: number;
  /** Unknown (not laid out yet): only the nominal limit applies. */
  available: number | null;
}) => {
  const nominal = nominalMaxHeight(visibleHeight);
  const limit = available == null ? nominal : Math.min(nominal, available);
  return Math.max(FIELD_MIN_PX, limit);
};

/** The field's height for its content, and whether it scrolls inside. */
export const fieldHeight = (contentHeight: number, maxHeight: number) => ({
  height: Math.max(FIELD_MIN_PX, Math.min(Math.ceil(contentHeight), maxHeight)),
  scrolls: contentHeight > maxHeight + 1,
});

/**
 * How much of the layout viewport the on-screen keyboard (or any other
 * overlay that shrinks the visual viewport) covers at the bottom, from the
 * visualViewport API.
 */
export const keyboardInset = ({
  layoutHeight,
  visualHeight,
  visualOffsetTop,
}: {
  layoutHeight: number;
  visualHeight: number;
  visualOffsetTop: number;
}) => Math.max(0, Math.round(layoutHeight - visualHeight - visualOffsetTop));
